
import React, { useState, useEffect } from 'react';
import { UserRole, RequestStatus, MeetingPlatform, ChatRequest, SubscriptionTier, SchedulingStatus } from './types.ts';
import { dbService } from './services/dbService.ts';

import LandingView from './views/LandingView.tsx';
import OnboardingView from './views/OnboardingView.tsx';
import StudentDashboard from './views/StudentDashboard.tsx';
import ProfessionalDashboard from './views/ProfessionalDashboard.tsx';
import AdminView from './views/AdminView.tsx';
import ChatView from './views/ChatView.tsx';
import EventsView from './views/EventsView.tsx';
import JobsView from './views/JobsView.tsx';
import CareerStrategistView from './views/CareerStrategistView.tsx';
import ProfessionalRewardsView from './views/ProfessionalRewardsView.tsx';
import ReciprocityHub from './views/ReciprocityHub.tsx';
import CalendarView from './views/CalendarView.tsx';
import HelpView from './views/HelpView.tsx';
import MembershipView from './views/MembershipView.tsx';
import ProfileView from './views/ProfileView.tsx';
import MeetingPrepView from './views/MeetingPrepView.tsx';
import AutomationView from './views/AutomationView.tsx';
import HelpChatbot from './components/HelpChatbot.tsx';
import DashboardLayout from './components/DashboardLayout.tsx';
import MeetingRoom from './views/MeetingRoom.tsx';
import AuthView from './views/AuthView.tsx';
import SageGuidedTour from './components/SageGuidedTour.tsx';

type AppState = 'landing' | 'auth_login' | 'auth_register' | 'onboarding' | 'dashboard' | 'admin';

const App = () => {
  const [currentUser, setCurrentUser] = useState(dbService.getCurrentUser());
  const [appState, setAppState] = useState<AppState>('dashboard');
  const [activeTab, setActiveTab] = useState('professionals'); 
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [isDemoMenuVisible, setIsDemoMenuVisible] = useState(true);
  const [activeMeeting, setActiveMeeting] = useState<ChatRequest | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showTour, setShowTour] = useState(false);

  // Default Demo User (Alex)
  const DEMO_STUDENT_EMAIL = 'alex.doe@stanford.edu';

  useEffect(() => {
    // 1. Seed Initial Data
    dbService.seedInitialData();

    // 2. Identity Resolution
    let user = dbService.findUserByEmail(DEMO_STUDENT_EMAIL);
    if (!user) {
      user = { 
        id: 'student_alex', 
        email: DEMO_STUDENT_EMAIL, 
        fullName: 'Alex Doe', 
        role: UserRole.STUDENT, 
        tier: SubscriptionTier.ADVANCED, 
        createdAt: Date.now() 
      };
      dbService.saveUser(user);
    }

    dbService.setCurrentUser(user);
    setCurrentUser(user);
    
    // Straight to dashboard for demo
    setAppState('dashboard');
    setActiveTab('professionals');
    setShowTour(true); // Auto-start tour in demo mode
    
    setIsInitializing(false);
  }, []);

  const handleLogout = () => {
    window.open('https://coffeeconnex.ca/', '_blank');
    dbService.setCurrentUser(null);
    setCurrentUser(null);
    setAppState('landing');
  };

  const switchPersona = (email: string) => {
    const user = dbService.findUserByEmail(email);
    if (!user) {
      alert("Persona switching unavailable in this reset state. Rebooting...");
      window.location.reload();
      return;
    }
    
    setSelectedChatId(null);
    dbService.setCurrentUser(user);
    setCurrentUser(user);
    
    if (user.role === UserRole.ADMIN) {
      setAppState('admin');
    } else {
      setAppState('dashboard');
      const defaultTab = user.role === UserRole.STUDENT ? 'professionals' : 'requests';
      setActiveTab(defaultTab);
      setShowTour(true); // Restart tour for new persona
    }
  };

  const handleAdminAccess = () => {
    const password = window.prompt("ADMIN PROTOCOL: Enter Authorization Key");
    if (password === "CoffeeConnex2025") {
      let admin = dbService.findUserByEmail('admin@coffeeconnex.ai');
      if (!admin) {
        admin = { id: 'admin_user', email: 'admin@coffeeconnex.ai', fullName: 'System Admin', role: UserRole.ADMIN, tier: SubscriptionTier.ADVANCED, createdAt: Date.now() };
        dbService.saveUser(admin);
      }
      dbService.setCurrentUser(admin);
      setCurrentUser(admin);
      setAppState('admin');
    } else if (password !== null) {
      alert("Unauthorized Access.");
    }
  };

  const onGoToChat = (requestId: string) => {
    setSelectedChatId(requestId);
    setActiveTab('chats');
  };

  const handleStartMeeting = (req: ChatRequest) => {
    setActiveMeeting(req);
  };

  const renderTabContent = () => {
    if (!currentUser) return null;
    const commonProps = { 
      user: currentUser, 
      onLogout: handleLogout, 
      onChangeTab: setActiveTab, 
      activeTab 
    };

    if (currentUser.role === UserRole.STUDENT) {
      switch (activeTab) {
        case 'professionals': return <StudentDashboard {...commonProps} onGoToChat={onGoToChat} />;
        case 'chats': return <ChatView {...commonProps} selectedId={selectedChatId} />;
        case 'prep': return <MeetingPrepView {...commonProps} />;
        case 'automation': return <AutomationView {...commonProps} />;
        case 'events': return <EventsView {...commonProps} />;
        case 'jobs': return <JobsView {...commonProps} />;
        case 'strategist': return <CareerStrategistView {...commonProps} />;
        case 'profile': return <ProfileView {...commonProps} />;
        case 'help': return <HelpView {...commonProps} />;
        case 'membership': return <MembershipView {...commonProps} user={currentUser} onChangeTab={setActiveTab} />;
        default: return <StudentDashboard {...commonProps} onGoToChat={onGoToChat} />;
      }
    } else if (currentUser.role === UserRole.PROFESSIONAL) {
      switch (activeTab) {
        case 'requests': return <ProfessionalDashboard {...commonProps} onAcceptAndChat={onGoToChat} onStartMeeting={handleStartMeeting} />;
        case 'chats': return <ChatView {...commonProps} selectedId={selectedChatId} />;
        case 'calendar': return <CalendarView {...commonProps} />;
        case 'rewards': return <ProfessionalRewardsView {...commonProps} />;
        case 'reciprocity': return <ReciprocityHub {...commonProps} onAcceptAndChat={onGoToChat} />;
        case 'strategist': return <CareerStrategistView {...commonProps} />;
        case 'profile': return <ProfileView {...commonProps} />;
        case 'help': return <HelpView {...commonProps} />;
        default: return <ProfessionalDashboard {...commonProps} onAcceptAndChat={onGoToChat} onStartMeeting={handleStartMeeting} />;
      }
    }
    return null;
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-50 dark:border-slate-800 border-t-blue-600 rounded-full animate-spin mb-4"></div>
        <p className="text-[10px] font-black text-blue-900 dark:text-blue-400 uppercase tracking-widest animate-pulse">Initializing Demo Protocol...</p>
      </div>
    );
  }

  if (activeMeeting && currentUser) {
    return <MeetingRoom user={currentUser} request={activeMeeting} onLeave={() => setActiveMeeting(null)} />;
  }

  switch (appState) {
    case 'admin':
      return <AdminView onLogout={handleLogout} />;
    
    case 'landing':
      return <LandingView onGetStarted={() => setAppState('auth_login')} onDemo={() => {
        const user = dbService.findUserByEmail(DEMO_STUDENT_EMAIL);
        setCurrentUser(user);
        setAppState('dashboard');
        setShowTour(true);
      }} />;

    case 'auth_login':
    case 'auth_register':
      return <AuthView 
        mode={appState === 'auth_login' ? 'login' : 'register'} 
        onSuccess={(user) => { setCurrentUser(user); setAppState('dashboard'); setShowTour(true); }} 
        onToggle={() => setAppState(appState === 'auth_login' ? 'auth_register' : 'auth_login')}
        onBack={() => setAppState('landing')}
      />;

    case 'dashboard':
    default:
      return currentUser ? (
        <DashboardLayout 
          role={currentUser.role} 
          activeTab={activeTab} 
          setActiveTab={setActiveTab} 
          onLogout={handleLogout} 
          hasUnread={dbService.hasUnreadMessages(currentUser.id, currentUser.role)}
        >
          {showTour && (
            <SageGuidedTour 
              role={currentUser.role} 
              setActiveTab={setActiveTab}
              onComplete={() => setShowTour(false)} 
            />
          )}
          {renderTabContent()}
          <HelpChatbot />
          
          {isDemoMenuVisible && (
            <div className="fixed bottom-64 right-8 md:bottom-32 z-[2000] flex flex-col items-end gap-3 pointer-events-none transition-all duration-500">
              <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-slate-200 dark:border-slate-800 p-4 rounded-[1.5rem] shadow-2xl animate-in fade-in slide-in-from-right-4 pointer-events-auto">
                <p className="text-[9px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-3">Demo Hub</p>
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <button onClick={() => switchPersona('alex.doe@stanford.edu')} className="px-4 py-2 bg-blue-50 dark:bg-slate-800 text-blue-700 dark:text-blue-400 rounded-xl text-[9px] font-black uppercase tracking-widest border border-blue-100 dark:border-slate-700 hover:bg-blue-600 hover:text-white transition">Student</button>
                    <button onClick={() => switchPersona('jordan.lee@demo.com')} className="px-4 py-2 bg-blue-50 dark:bg-slate-800 text-blue-700 dark:text-blue-400 rounded-xl text-[9px] font-black uppercase tracking-widest border border-blue-100 dark:border-slate-700 hover:bg-blue-600 hover:text-white transition">Pro</button>
                    <button onClick={handleAdminAccess} className="w-8 h-8 flex items-center justify-center bg-slate-900 dark:bg-slate-700 text-white rounded-xl hover:bg-blue-600 transition"><i className="fas fa-shield-halved text-[10px]"></i></button>
                  </div>
                  <button 
                    onClick={() => {
                      const defaultTab = currentUser.role === UserRole.STUDENT ? 'professionals' : 'requests';
                      setActiveTab(defaultTab);
                      setShowTour(true);
                    }} 
                    className="w-full py-2 bg-indigo-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg hover:bg-indigo-500 transition flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-play text-[8px]"></i> Restart Briefing
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <button 
            onClick={() => setIsDemoMenuVisible(!isDemoMenuVisible)}
            className="fixed bottom-44 right-8 md:bottom-8 w-14 h-14 bg-white dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition z-[2001] text-slate-400 hover:text-blue-600"
          >
            <i className={`fas ${isDemoMenuVisible ? 'fa-eye-slash' : 'fa-wand-magic-sparkles'}`}></i>
          </button>
        </DashboardLayout>
      ) : (
        <LandingView onGetStarted={() => setAppState('auth_login')} onDemo={() => {
          const user = dbService.findUserByEmail(DEMO_STUDENT_EMAIL);
          setCurrentUser(user);
          setAppState('dashboard');
          setShowTour(true);
        }} />
      );
  }
};

export default App;
